/**
 * Generic Bulk Upload Drawer Component
 *
 * Reusable drawer for all bulk upload operations:
 * - Bulk Data Fetch
 * - Bulk Revoke Consent
 * - Bulk Create Consent
 *
 * Usage:
 * <GenericBulkUploadDrawer
 *   config={BULK_DATA_FETCH_CONFIG}
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   onUploadSuccess={onSuccess}
 * />
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, CheckCircle, Info, Lightbulb, Download, RefreshCw, X } from 'lucide-react';
import { Button, Drawer, DrawerContent, DrawerHeader, DrawerOverlay, DatePicker } from 'equal-ds-ui';
import * as XLSX from 'xlsx';
import { bulkMisAPI } from '../../../services/bulk-mis-api.service';
import { validateFileContent } from '../../../utils/bulk-upload-validators';
import type { BulkUploadDrawerProps } from '../../../types/bulk-upload';

export const GenericBulkUploadDrawer: React.FC<BulkUploadDrawerProps> = ({
  config,
  isOpen,
  onClose,
  onUploadSuccess,
}) => {
  // Form state
  const [referenceId, setReferenceId] = useState('');
  const [referenceIdError, setReferenceIdError] = useState<string>('');
  const [configId, setConfigId] = useState('');
  const [fiStartDate, setFiStartDate] = useState<Date | null>(null);
  const [fiEndDate, setFiEndDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState<string>('');

  // Phase 1: Output options state
  const [enableAnalytics, setEnableAnalytics] = useState(false);
  const [enableEws, setEnableEws] = useState(false);
  const [outputFormats, setOutputFormats] = useState<string[]>(['json']);

  // Duplicate reference ID dialog state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [suggestedReferenceId, setSuggestedReferenceId] = useState<string | null>(null);

  // File state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isValidFile, setIsValidFile] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAbortController = useRef<AbortController | null>(null);

  // Configuration
  const allowedFileTypes = ['.csv', '.json', '.xls', '.xlsx', '.xlsm', '.xlsb', '.ods'];
  const maxFileSizeInMB = config.maxFileSizeMB || (sessionStorage.getItem('bulkPipelineUploadFileSizeLimitInMB')
    ? parseInt(sessionStorage.getItem('bulkPipelineUploadFileSizeLimitInMB') || '10')
    : 10);
  const maxRecordLimit = config.maxRecordLimit || (sessionStorage.getItem('bulkUploadMaxRecordLimit')
    ? parseInt(sessionStorage.getItem('bulkUploadMaxRecordLimit') || '10000')
    : 10000);

  // Close modal
  const closeModal = () => {
    onClose();
    resetForm();
  };

  // Reset form
  const resetForm = () => {
    setReferenceId('');
    setReferenceIdError('');
    setConfigId('');
    setFiStartDate(null);
    setFiEndDate(null);
    setDateError('');
    setFileUploaded(false);
    setSelectedFile(null);
    setIsValidFile(false);
    setErrorMessages([]);
    setSubmissionSuccess(false);
    setShowDuplicateDialog(false);
    setSuggestedReferenceId(null);
    // Reset output options
    setEnableAnalytics(false);
    setEnableEws(false);
    setOutputFormats(['json']);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Validate date range
  const validateDateRange = (startDate: Date | null, endDate: Date | null) => {
    if (startDate && endDate && startDate > endDate) {
      setDateError('Start date cannot be greater than end date');
    } else {
      setDateError('');
    }
  };

  // Handle start date change
  const handleStartDateChange = (date: Date | null) => {
    setFiStartDate(date);
    validateDateRange(date, fiEndDate);
  };

  // Handle end date change
  const handleEndDateChange = (date: Date | null) => {
    setFiEndDate(date);
    validateDateRange(fiStartDate, date);
  };

  // Handle reference ID change with validation
  const handleReferenceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const refConfig = config.additionalFields?.referenceId;

    if (!refConfig) {
      setReferenceId(value);
      return;
    }

    setReferenceId(value);

    // Real-time validation
    if (value.length === 0) {
      setReferenceIdError('');
      return;
    }

    // Pattern validation
    if (refConfig.pattern && !refConfig.pattern.test(value)) {
      setReferenceIdError(refConfig.errorMessage || 'Invalid format');
      return;
    }

    // Length validation
    if (refConfig.minLength && value.length < refConfig.minLength) {
      setReferenceIdError(`Must be at least ${refConfig.minLength} characters`);
      return;
    }

    if (refConfig.maxLength && value.length > refConfig.maxLength) {
      setReferenceIdError(`Cannot exceed ${refConfig.maxLength} characters`);
      return;
    }

    setReferenceIdError('');
  };

  // Reset form when drawer opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Count records in file
  const countRecordsInFile = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const reader = new FileReader();

      reader.onerror = () => reject(new Error('Failed to read file'));

      if (fileExtension === '.csv') {
        reader.onload = (event: any) => {
          try {
            const text = event.target.result as string;
            const lines = text.split(/\r\n|\n|\r/).filter(line => line.trim().length > 0);
            const recordCount = Math.max(0, lines.length - 1);
            resolve(recordCount);
          } catch (error) {
            reject(new Error('Failed to parse CSV file'));
          }
        };
        reader.readAsText(file);
      } else if (fileExtension === '.json') {
        reader.onload = (event: any) => {
          try {
            const text = event.target.result as string;
            const jsonData = JSON.parse(text);
            const recordCount = Array.isArray(jsonData) ? jsonData.length : 0;
            resolve(recordCount);
          } catch (error) {
            reject(new Error('Failed to parse JSON file'));
          }
        };
        reader.readAsText(file);
      } else {
        reader.onload = (event: any) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array', bookVBA: true });

            if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
              resolve(0);
              return;
            }

            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
            const recordCount = Math.max(0, jsonData.length - 1);
            resolve(recordCount);
          } catch (error) {
            reject(new Error('Failed to parse Excel file'));
          }
        };
        reader.readAsArrayBuffer(file);
      }
    });
  };

  // File validation
  const validateFile = (file: File): Promise<string[]> => {
    return new Promise(async (resolve) => {
      const errors: string[] = [];

      // 1. Filename validation
      const trimmedFileName = file.name.trim();
      const validFileNamePattern = /^[a-zA-Z0-9_.\- ()]+$/;
      if (!validFileNamePattern.test(trimmedFileName)) {
        errors.push('Invalid file name. Only alphanumeric, hyphen, underscore, space, dot, and parentheses are allowed');
      }

      // 2. File size validation
      if (file.size > maxFileSizeInMB * 1024 * 1024) {
        errors.push(`File size exceeds ${maxFileSizeInMB}MB limit`);
      }

      // 3. Record count validation
      try {
        const recordCount = await countRecordsInFile(file);
        if (recordCount > maxRecordLimit) {
          errors.push(`File contains ${recordCount.toLocaleString()} records. Maximum ${maxRecordLimit.toLocaleString()} records allowed`);
        }
      } catch (error) {
        console.error('Error counting records:', error);
      }

      // 4. Content validation using generic validator
      try {
        const contentValidation = await validateFileContent(file, config);
        errors.push(...contentValidation.errors);
      } catch (error) {
        console.error('Error validating file content:', error);
        errors.push('Failed to validate file content');
      }

      // 5. Check file extension
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (fileExtension === '.csv' || fileExtension === '.json') {
        resolve(errors);
        return;
      }

      // 6. File corruption check (Excel files)
      const reader = new FileReader();
      reader.onload = (event: any) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array', bookVBA: true });

          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            errors.push('File is corrupted or empty');
          }
        } catch (error) {
          errors.push('File is password protected or corrupted');
        }
        resolve(errors);
      };

      reader.onerror = () => {
        errors.push('Failed to read file');
        resolve(errors);
      };

      reader.readAsArrayBuffer(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      setErrorMessages(['Please select a file']);
      setIsValidFile(false);
      setFileUploaded(false);
      return;
    }

    await processFile(file);
  };

  // Process file
  const processFile = async (file: File) => {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedFileTypes.includes(fileExtension)) {
      setErrorMessages([`Invalid file type. Allowed types: ${allowedFileTypes.join(', ')}`]);
      setIsValidFile(false);
      setFileUploaded(false);
      setSelectedFile(null);
      return;
    }

    const errors = await validateFile(file);

    if (errors.length > 0) {
      setErrorMessages(errors);
      setIsValidFile(false);
      setFileUploaded(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setIsValidFile(true);
      setUploadedFileName(file.name);
      setFileUploaded(true);
      setSelectedFile(file);
      setErrorMessages([]);
    }
  };

  // Remove file
  const removeFile = () => {
    setUploadedFileName('');
    setFileUploaded(false);
    setIsValidFile(false);
    setSelectedFile(null);
    setErrorMessages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Re-upload file
  const reUploadFile = () => {
    removeFile();
    fileInputRef.current?.click();
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) {
      setIsDragOver(true);
    }
  }, [isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  }, [isUploading]);

  // Submit file upload. overrideReferenceId avoids stale closure when "Use Suggested" is clicked.
  const submitDetails = async (overrideReferenceId?: string) => {
    const effectiveReferenceId = overrideReferenceId || referenceId;
    const refConfig = config.additionalFields?.referenceId;

    // Validate reference ID if required
    if (refConfig?.required && !effectiveReferenceId.trim()) {
      setErrorMessages(['Reference ID is required']);
      return;
    }

    if (referenceIdError && !overrideReferenceId) {
      setErrorMessages([referenceIdError]);
      return;
    }

    if (!fileUploaded || !isValidFile || !selectedFile) {
      setErrorMessages(['Please select a valid file before submitting']);
      return;
    }

    // Validate date range if configured
    if (config.additionalFields?.dateRange?.validateRange && fiStartDate && fiEndDate && fiStartDate > fiEndDate) {
      setErrorMessages(['Start date must be before or equal to end date']);
      return;
    }

    try {
      setIsUploading(true);
      setSubmissionSuccess(false);
      setErrorMessages([]);

      // Check for duplicate reference ID before uploading (data_fetch only)
      if (config.screenName === 'data_fetch' && effectiveReferenceId.trim()) {
        try {
          const checkResult = await bulkMisAPI.checkReferenceId(effectiveReferenceId.trim());
          if (checkResult.exists) {
            setIsUploading(false);
            setSuggestedReferenceId(checkResult.suggestion);
            setShowDuplicateDialog(true);
            return;
          }
        } catch (checkError) {
          // If check fails, proceed with upload — server will catch duplicates
          console.error('Reference ID check failed, proceeding with upload:', checkError);
        }
      }

      uploadAbortController.current = new AbortController();

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('screenName', config.screenName);

      if (effectiveReferenceId.trim()) {
        formData.append('reference_id', effectiveReferenceId.trim());
      }

      // Build global_config if needed
      const hasGlobalConfig = configId.trim() || fiStartDate || fiEndDate || enableAnalytics || enableEws;
      if (hasGlobalConfig) {
        const globalConfig: Record<string, any> = {};

        if (fiStartDate) {
          globalConfig.fiDataRangeFrom = fiStartDate.toISOString();
        }
        if (fiEndDate) {
          globalConfig.fiDataRangeTo = fiEndDate.toISOString();
        }
        if (configId.trim()) {
          globalConfig.configId = configId.trim();
        }

        // Phase 1: Add output options
        if (enableAnalytics || enableEws) {
          globalConfig.output = {
            analytics: enableAnalytics,
            ews: enableEws,
            format: outputFormats
          };
        }

        formData.append('global_config', JSON.stringify(globalConfig));
      }

      await bulkMisAPI.uploadFile(formData, uploadAbortController.current.signal);

      setIsUploading(false);
      setSubmissionSuccess(true);

      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error: unknown) {
      setIsUploading(false);
      setSubmissionSuccess(false);

      // Handle different error types
      // httpClient rejects with ApiError { message, status, data }
      // raw axios rejects with AxiosError { response: { status, data } }
      const err = error as any;
      const isAbort = err?.name === 'AbortError' || err?.name === 'CanceledError';
      const status = err?.response?.status || err?.status;
      const responseData = err?.response?.data || err?.data;

      if (isAbort) {
        setErrorMessages(['File upload cancelled']);
      } else if (status === 409 || responseData?.errorCode === 'DuplicateReferenceId') {
        // Duplicate reference_id — show suggestion dialog if available
        const suggestion = responseData?.suggestion;
        if (suggestion) {
          setSuggestedReferenceId(suggestion);
          setShowDuplicateDialog(true);
        } else {
          setErrorMessages(['This Reference ID already exists. Please use a different one.']);
        }
      } else {
        const errorMsg = responseData?.errorMsg || responseData?.message
          || (error instanceof Error ? error.message : null)
          || err?.message
          || 'Failed to upload file. Please try again.';
        setErrorMessages([errorMsg]);
      }
    }
  };

  // Cancel upload
  const cancelUpload = () => {
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
      uploadAbortController.current = null;
    }
    setIsUploading(false);
    closeModal();
  };

  // Download sample template.
  // Sample format is version-aware: V2 (bulk-orchestration/BOS) is CSV-only, while the
  // legacy V1 path parses an xlsx workbook. Prefer the V2 variant when the tenant is on V2,
  // else fall back to the V1 default — consistent with BulkMisUpload's downloadSampleFile.
  const downloadSampleFile = () => {
    const isV2 = sessionStorage.getItem('isBulkOrchestrationV2Enabled') === 'true';
    const link = document.createElement('a');
    link.href = isV2 && config.sampleTemplateUrlV2 ? config.sampleTemplateUrlV2 : config.sampleTemplateUrl;
    link.download =
      isV2 && config.sampleTemplateFilenameV2 ? config.sampleTemplateFilenameV2 : config.sampleTemplateFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if form is valid
  const isFormValid =
    (!config.additionalFields?.referenceId?.required || (referenceId.trim() && !referenceIdError)) &&
    isValidFile &&
    fileUploaded &&
    !dateError;

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="70%"
        className="border-border-default bg-gradient-to-b from-background-secondary to-primary-50"
      >
        <DrawerHeader
          title={`UPLOAD ${config.operationDisplayName.toUpperCase()} BATCH`}
          subtitle={config.description}
          icon={<Upload className="w-5 h-5" />}
          onClose={closeModal}
        />

        <DrawerContent className="equal-ds-root flex flex-col flex-1 overflow-y-auto p-6 space-y-4">
          {submissionSuccess ? (
            <div className="flex-1 flex items-center justify-center min-h-[300px]">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-lg font-semibold text-gray-900 mb-1">Upload Successful</p>
                <p className="text-sm text-gray-500 mb-4">Your batch has been uploaded successfully</p>
                <Button onClick={closeModal} variant="primary">
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Form Fields */}
              <div className="space-y-3">
                {/* Reference ID */}
                {config.additionalFields?.referenceId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {config.additionalFields.referenceId.label}{' '}
                      {config.additionalFields.referenceId.required && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={referenceId}
                      onChange={handleReferenceIdChange}
                      placeholder={config.additionalFields.referenceId.placeholder}
                      className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 ${
                        referenceIdError
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      disabled={isUploading}
                    />
                    {referenceIdError && (
                      <p className="text-xs text-red-600 mt-1">{referenceIdError}</p>
                    )}
                  </div>
                )}

                {/* Config ID and Date Range in row */}
                {(config.additionalFields?.configId || config.additionalFields?.dateRange) && (
                  <div className="grid grid-cols-3 gap-3">
                    {config.additionalFields?.configId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {config.additionalFields.configId.label}
                        </label>
                        <input
                          type="text"
                          value={configId}
                          onChange={(e) => setConfigId(e.target.value)}
                          placeholder={config.additionalFields.configId.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={isUploading}
                        />
                      </div>
                    )}
                    {config.additionalFields?.dateRange && sessionStorage.getItem('isBulkOrchestrationV2Enabled') !== 'true' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {config.additionalFields.dateRange.fromLabel}
                          </label>
                          <DatePicker
                            selectedDate={fiStartDate}
                            onChange={handleStartDateChange}
                            placeholder="dd/mm/yyyy"
                            disabled={isUploading}
                            triggerClassName="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {config.additionalFields.dateRange.toLabel}
                          </label>
                          <DatePicker
                            selectedDate={fiEndDate}
                            onChange={handleEndDateChange}
                            placeholder="dd/mm/yyyy"
                            disabled={isUploading}
                            triggerClassName="w-full"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
                {dateError && (
                  <p className="text-xs text-red-600 mt-1">{dateError}</p>
                )}

                {/* Phase 1: Output Options */}
                {config.additionalFields?.outputOptions?.enabled && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      {config.additionalFields.outputOptions.label}
                    </h4>
                    {config.additionalFields.outputOptions.helpText && (
                      <p className="text-xs text-gray-600 mb-3">
                        {config.additionalFields.outputOptions.helpText}
                      </p>
                    )}

                    <div className="space-y-2">
                      {/* Analytics checkbox */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enableAnalytics}
                          onChange={(e) => setEnableAnalytics(e.target.checked)}
                          disabled={isUploading}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          {config.additionalFields.outputOptions.analyticsLabel || 'Enable Analytics'}
                        </span>
                      </label>

                      {/* EWS checkbox */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enableEws}
                          onChange={(e) => setEnableEws(e.target.checked)}
                          disabled={isUploading}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          {config.additionalFields.outputOptions.ewsLabel || 'Enable EWS'}
                        </span>
                      </label>

                      {/* Format selector */}
                      {(enableAnalytics || enableEws) && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {config.additionalFields.outputOptions.formatLabel || 'Output Format'}
                          </label>
                          <div className="space-y-1.5">
                            {['json', 'excel', 'pdf', 'dashboard'].map((format) => (
                              <label key={format} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={outputFormats.includes(format)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setOutputFormats([...outputFormats, format]);
                                    } else {
                                      setOutputFormats(outputFormats.filter(f => f !== format));
                                    }
                                  }}
                                  disabled={isUploading}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 capitalize">{format}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Tips */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">Tips for uploading</h4>
                    <p className="text-xs text-gray-600 mb-2">
                      Ensure the file you upload has the correct layout. Download our template to get started.
                    </p>
                    <button
                      onClick={downloadSampleFile}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download Template
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload File Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  UPLOAD FILE
                </label>

                {!fileUploaded ? (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`
                      relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200
                      ${isDragOver
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                      }
                      ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                  >
                    <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                    <p className="text-sm text-gray-600 mb-1">
                      {isDragOver ? 'Drop your file here' : 'Drag & drop your file here or click to browse'}
                    </p>
                    <p className="text-xs text-gray-400">
                      Supported: CSV, JSON, Excel (Max {maxFileSizeInMB}MB, {maxRecordLimit.toLocaleString()} records)
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={allowedFileTypes.join(',')}
                      onChange={handleFileSelect}
                      disabled={isUploading}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm font-medium text-gray-900">{uploadedFileName}</span>
                      <button
                        onClick={reUploadFile}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Re-upload file"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={removeFile}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove file"
                      >
                        <X className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                )}

                {errorMessages.length > 0 && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                    {errorMessages.map((error, index) => (
                      <p key={index} className="text-xs text-red-700">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Instructions</h4>
                    <ul className="space-y-1 text-xs text-gray-600">
                      <li className="flex items-start gap-1.5">
                        <span>•</span>
                        <span>
                          Formats: <span className="font-semibold">.xls, .xlsx, .xlsm, .xlsb, .ods, .csv, .json</span>
                        </span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span>•</span>
                        <span>Filename: alphanumeric, hyphen, underscore, space, dot, parentheses only</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span>•</span>
                        <span>File must <span className="font-semibold">not be password protected</span></span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span>•</span>
                        <span>Max file size: <span className="font-semibold">{maxFileSizeInMB} MB</span></span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span>•</span>
                        <span>Max records: <span className="font-semibold">{maxRecordLimit.toLocaleString()}</span> per file</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 pt-2 pb-2 sticky bottom-0 bg-gradient-to-t from-white via-white">
                <Button
                  onClick={isUploading ? cancelUpload : () => submitDetails()}
                  disabled={!isFormValid && !isUploading}
                  variant="secondary"
                  className="w-full flex items-center justify-center gap-2"
                >
                  {isUploading && (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  )}
                  {isUploading ? 'UPLOADING...' : 'UPLOAD BATCH'}
                </Button>
              </div>
            </>
          )}
        </DrawerContent>
      </Drawer>

      {/* Upload Overlay */}
      {isUploading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg p-4 shadow-xl">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-900">Uploading file...</p>
          </div>
        </div>
      )}

      {/* Duplicate Reference ID Dialog */}
      {showDuplicateDialog && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl p-6 shadow-2xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reference ID Already Exists</h3>
            <p className="text-sm text-gray-600 mb-4">
              The reference ID <span className="font-semibold">"{referenceId}"</span> is already in use.
              {suggestedReferenceId && (
                <> We suggest using <span className="font-semibold">"{suggestedReferenceId}"</span> instead.</>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDuplicateDialog(false);
                  setSuggestedReferenceId(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Edit Manually
              </button>
              {suggestedReferenceId && (
                <button
                  onClick={() => {
                    const suggested = suggestedReferenceId!;
                    setReferenceId(suggested);
                    setReferenceIdError('');
                    setShowDuplicateDialog(false);
                    setSuggestedReferenceId(null);
                    // Pass directly to avoid stale closure
                    submitDetails(suggested);
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Use Suggested
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
